import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import {
  CurrentStage,
  ExternalService,
  IntegrationType,
  type Integration,
  type IntegrationEntry,
} from '../../api/integrations-client/integrations-client.type';
import { IntegrationStatus, type IntegrationProject } from './integrations.type';

dayjs.extend(customParseFormat);
dayjs.extend(duration);

/**
 * Returns the integration status based on the given integration entry.
 *
 * @param {IntegrationEntry} integration - The integration entry to check the status for.
 * @returns {IntegrationStatus} The status of the integration.
 */
const getIntegrationStatus = ({ id, last_run_status, last_completed_at }: IntegrationEntry): IntegrationStatus => {
  if (last_run_status && ['401', '403'].includes(last_run_status) && !isIntegrationReauthCompleted(id)) {
    return IntegrationStatus.Expired;
  }

  const isStale = dayjs.duration(dayjs().diff(last_completed_at)).asHours() > 6;

  if (isStale) {
    return IntegrationStatus.Stale;
  }

  return IntegrationStatus.Operational;
};

/**
 * Retrieves a message indicating the refresh status based on the provided IntegrationProject and optional refresh status.
 *
 * @param {IntegrationProject} project - an object containing the properties manually_prioritized and current_stage
 * @param {number} refreshStatus - an optional number representing the refresh status
 * @returns {string | null} a message indicating the refresh status, or null if no message is available
 */
const getRefreshMessage = (
  { manually_prioritized, current_stage }: IntegrationProject,
  refreshStatus?: number,
): string | null => {
  const isQueued = manually_prioritized && current_stage === CurrentStage.WAITING;
  const isRunning = current_stage === CurrentStage.RUNNING;

  if (refreshStatus === 200 || isQueued) {
    return 'Refresh in queue';
  }

  if (isRunning) {
    return 'Refresh in progress';
  }

  return null;
};

/**
 * Sets the integration ID for re-authentication in the local storage.
 *
 * @param {string} id - The ID of the integration to be stored
 * @returns {void}
 */
const setIntegrationForReauth = (id: string): void => {
  // Guarded because this writes a NEW key: on an origin at its Web Storage quota
  // it throws, and the caller runs it immediately before `authenticate()` — an
  // unguarded throw there leaves the Re-authenticate button doing nothing at all.
  // Losing the marker only costs the post-OAuth "reauth completed" toast.
  try {
    localStorage.setItem('reauth-integration-active', JSON.stringify(id));
  } catch (error) {
    console.warn('Failed to mark the integration for re-authentication; continuing.', error);
  }
};

/**
 * Retrieves current active integration for re-authentication from local storage, if available.
 *
 * @returns {string | null} The integration for re-authentication, or null if not available.
 */
const getIntegrationForReauth = (): string | null => {
  // Guarded read: this supplies the integration id to `completeOauth`, and
  // `completeReauth` runs it on the OAuth return path. An unguarded throw from a
  // corrupt value or blocked storage would strand the user on the callback URL.
  try {
    const cachedValue = localStorage.getItem('reauth-integration-active');

    if (cachedValue) {
      return JSON.parse(cachedValue);
    }
  } catch (error) {
    console.warn('Failed to read the integration marked for re-authentication; treating it as absent.', error);
  }

  return null;
};

/**
 * Removes the current active integration for re-authentication from local storage.
 *
 * @returns {void}
 */
const removeIntegrationForReauth = (): void => {
  try {
    localStorage.removeItem('reauth-integration-active');
  } catch (error) {
    console.warn('Failed to clear the re-authentication marker; continuing.', error);
  }
};

/**
 * Completes re-authentication process for the integration.
 *
 * @returns {void}
 */
const completeReauth = (): void => {
  const id = getIntegrationForReauth();

  if (!id) {
    return;
  }

  removeIntegrationForReauth();
  setIntegrationReauthCompleted(id);
};

/**
 * Marks the given integration as re-authentication completed.
 *
 * @param {string} id - The ID of the integration reauthorization
 * @returns {void}
 */
const setIntegrationReauthCompleted = (id: string): void => {
  const ids = getIntegrationsReauthCompleted();

  try {
    localStorage.setItem('reauth-integration-completed', JSON.stringify([...ids, id]));
  } catch (error) {
    console.warn('Failed to record the completed re-authentication; continuing.', error);
  }
};

/**
 * Checks if the integration re-authentication is completed for the given ID.
 *
 * @param {string} id - The ID to check for re-authentication completion
 * @returns {boolean} Whether the re-authentication is completed for the given ID
 */
const isIntegrationReauthCompleted = (id: string): boolean => {
  const ids = getIntegrationsReauthCompleted();

  return ids.includes(id);
};

/**
 * Retrieves the array of integration IDs that have been completed re-authentication.
 *
 * @returns {string[]} The array of integration IDs.
 */
const getIntegrationsReauthCompleted = (): string[] => {
  // Guarded, and shape-checked: this runs first inside `setIntegrationReauthCompleted`,
  // which `completeReauth` calls before navigating off the OAuth callback. A throw
  // here — or a non-array value spread into the write below — would break that
  // navigation and leave the user stuck on the callback URL.
  try {
    const cachedIds = localStorage.getItem('reauth-integration-completed');
    const parsed: unknown = cachedIds ? JSON.parse(cachedIds) : null;

    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    console.warn('Failed to read the completed re-authentications; treating the list as empty.', error);
  }

  return [];
};

/**
 * Maps integration type strings to their corresponding ExternalService enum values.
 * Used by the OAuth return handler to determine which service to use for authentication.
 *
 * @param {IntegrationType} integrationType - The integration type from URL or configuration
 * @returns {ExternalService | null} The corresponding ExternalService enum value, or null if not found
 */
const getServiceForIntegrationType = (integrationType: IntegrationType): ExternalService | null => {
  const services: Partial<Record<IntegrationType, ExternalService>> = {
    [IntegrationType.JIRA]: ExternalService.Jira,
    [IntegrationType.JDC]: ExternalService.JDC,
    [IntegrationType.GITHUB]: ExternalService.Github,
    [IntegrationType.ADO]: ExternalService.ADO,
    [IntegrationType.LINEAR]: ExternalService.Linear,
  };

  return services[integrationType] ?? null;
};

/**
 * Determines if the given integration is a JDC (Jira Data Center) integration.
 *
 * @param {Integration | null} integration - The integration object to check.
 * @returns {boolean} True if the integration is JDC or Jira Data Center, false otherwise.
 */
const isJDCIntegration = (integration: Integration | null): boolean => {
  if (!integration) {
    return false;
  }

  const integrationName = integration.name.toLowerCase();
  return integrationName === 'jdc' || integrationName === 'jira_data_center';
};

/**
 * Safely extracts and validates the integration type from an integration object.
 * Returns the IntegrationType if valid, or null if the integration is null or has an invalid type.
 *
 * @param {Integration | null} integration - The integration object to extract the type from.
 * @returns {IntegrationType | null} The validated IntegrationType, or null if invalid.
 */
const getIntegrationType = (integration: Integration | null): IntegrationType | null => {
  if (!integration?.name) {
    return null;
  }

  const normalizedName = integration.name.toLowerCase();

  // Check if the normalized name matches any IntegrationType enum value
  const validTypes = Object.values(IntegrationType) as string[];
  if (validTypes.includes(normalizedName)) {
    return normalizedName as IntegrationType;
  }

  return null;
};

export {
  completeReauth,
  getIntegrationForReauth,
  getIntegrationStatus,
  getIntegrationType,
  getRefreshMessage,
  getServiceForIntegrationType,
  isJDCIntegration,
  setIntegrationForReauth,
};
